import type { MapObject } from '@warplan/shared';
import { getIconEntry } from '../lib/icon-catalog.js';

export interface MapMarkersProps {
  objects: ReadonlyArray<MapObject>;
  /** Inverse-scale factor (atlas-pixel-to-icon-pixel ratio). */
  iconInv: number;
  iconSize?: number;
  selectedId?: string | null;
  /** Id of the marker currently being position-dragged. */
  draggingId?: string | null;
  /** Live world position of the marker being dragged. */
  draggingPosition?: { x: number; y: number };
  /** Id of the marker currently being resized. */
  resizingId?: string | null;
  /** Live scale of the marker being resized (0.5–1.5). */
  resizingScale?: number;
  /**
   * Pre-computed de-overlapped render positions keyed by marker id.
   * When provided, icons are rendered at these positions instead of their
   * authoritative world positions. Hit-test callbacks should also use these
   * positions so that visual placement matches interactable area.
   * Falls back to the marker's own position when an id is absent.
   */
  adjustedPositions?: ReadonlyMap<string, { x: number; y: number }>;
}

const DEFAULT_SIZE = 32;
export const MIN_MARKER_SCALE = 1.0;
export const MAX_MARKER_SCALE = 1.5;
export { DEFAULT_SIZE as DEFAULT_ICON_SIZE };

/**
 * Width (screen pixels) of the edge strip used for resize interaction.
 * The outer EDGE_ZONE_PX of the icon activates resize, the rest activates drag.
 */
export const EDGE_ZONE_PX = 3;

export function MapMarkers({
  objects,
  iconInv,
  iconSize = DEFAULT_SIZE,
  selectedId,
  draggingId,
  draggingPosition,
  resizingId,
  resizingScale,
  adjustedPositions,
}: MapMarkersProps): React.JSX.Element {
  const sorted = [...objects].sort(
    (a, b) => new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime(),
  );
  return (
    <>
      {sorted.map((obj) => {
        const entry = getIconEntry(obj.kind, obj.metadata.iconId);
        const isSelected = obj.id === selectedId;
        const isDragging = obj.id === draggingId;
        const isResizing = obj.id === resizingId;

        const storedScale = obj.metadata.scale ?? 1;
        const liveScale = isResizing && resizingScale != null ? resizingScale : storedScale;
        const size = iconSize * iconInv * liveScale;
        const halfSize = size / 2;

        // Inner (drag) zone: center portion of the icon.
        // Edge strip = outer EDGE_ZONE_PX screen-pixels → EDGE_ZONE_PX * iconInv world-units.
        const edgeWorld = EDGE_ZONE_PX * iconInv;
        const innerHalf = Math.max(halfSize - edgeWorld, halfSize * 0.45);
        const innerSize = innerHalf * 2;

        // `dragPos` = authoritative position used as drag anchor / resize origin.
        const dragPos = isDragging && draggingPosition ? draggingPosition : obj.position;
        // `pos` = de-overlapped render position used for ALL visual rects
        // (image, selection border, hit-test zones). Falls back to dragPos
        // when dragging/resizing (image must follow cursor) or when no adjusted
        // position was provided for this id.
        const pos =
          isDragging || isResizing ? dragPos : (adjustedPositions?.get(obj.id) ?? dragPos);

        const tooltip = obj.metadata.note ? `${entry.label} — ${obj.metadata.note}` : entry.label;

        return (
          <g key={obj.id}>
            <title>{tooltip}</title>
            {/* Selection / drag / resize border */}
            {(isSelected || isDragging || isResizing) && (
              <rect
                x={pos.x - halfSize - 2 * iconInv}
                y={pos.y - halfSize - 2 * iconInv}
                width={size + 4 * iconInv}
                height={size + 4 * iconInv}
                rx={4 * iconInv}
                ry={4 * iconInv}
                fill="none"
                stroke={isDragging ? '#fbbf24' : isResizing ? '#a78bfa' : '#6ee7b7'}
                strokeWidth={2 * iconInv}
                strokeDasharray={isDragging || isResizing ? `${4 * iconInv}` : undefined}
                pointerEvents="none"
              />
            )}

            {/* Icon image */}
            <image
              href={entry.src}
              x={pos.x - halfSize}
              y={pos.y - halfSize}
              width={size}
              height={size}
              preserveAspectRatio="xMidYMid meet"
              pointerEvents="none"
              opacity={isDragging ? 0.75 : 1}
            />

            {/*
             * Hit-area layer (rendered after image so z-order is above it).
             * For selected, non-dragging markers:
             *   1. Full-size rect → resize cursor on edges (z: low)
             *   2. Center rect    → drag cursor on center  (z: high, overrides edges)
             * For all other markers:
             *   1. Full-size rect → drag cursor (entire icon area)
             */}
            {isSelected && !isDragging ? (
              <>
                {/* Edge zone → resize cursor */}
                <rect
                  x={pos.x - halfSize}
                  y={pos.y - halfSize}
                  width={size}
                  height={size}
                  fill="transparent"
                  pointerEvents="fill"
                  style={{ cursor: 'nwse-resize' }}
                />
                {/* Center zone → drag cursor (covers center, overrides resize cursor) */}
                <rect
                  x={pos.x - innerHalf}
                  y={pos.y - innerHalf}
                  width={innerSize}
                  height={innerSize}
                  fill="transparent"
                  pointerEvents="fill"
                  style={{ cursor: 'grab' }}
                />
              </>
            ) : (
              <rect
                x={pos.x - halfSize}
                y={pos.y - halfSize}
                width={size}
                height={size}
                fill="transparent"
                pointerEvents="fill"
                style={{ cursor: isDragging ? 'grabbing' : 'pointer' }}
              />
            )}
          </g>
        );
      })}
    </>
  );
}
